import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, where, getDocs, orderBy, limit, startAfter } from 'firebase/firestore';
import PageHeader from '../components/PageHeader';
import AnimatedSection from '../components/AnimatedSection';
import Lightbox from "yet-another-react-lightbox";
import Captions from "yet-another-react-lightbox/plugins/captions";
import Download from "yet-another-react-lightbox/plugins/download";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen";
import Counter from "yet-another-react-lightbox/plugins/counter";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/captions.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import "yet-another-react-lightbox/plugins/counter.css";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import { FaArrowLeft, FaHeart, FaRegHeart, FaList, FaShareAlt, FaCheck } from 'react-icons/fa';
import '../components/styles.css';
import NotFoundPage from './NotFoundPage';

const GalleryImage = ({ image, onClick, isFavorite, onToggleFavorite }) => {
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef();

  useEffect(() => {
    if (imgRef.current && imgRef.current.complete) {
      setLoaded(true);
    }
  }, []);

  return (
    <div className={`gallery-item ${!loaded ? 'skeleton' : ''}`} onClick={onClick}>
      <button
        className={`btn-bookmark ${isFavorite ? 'active' : ''}`}
        onClick={(e) => onToggleFavorite(e, image.id)}
        title={isFavorite ? 'Hapus dari favorit' : 'Tambah ke favorit'}
      >
        {isFavorite ? <FaHeart /> : <FaRegHeart />}
      </button>
      <img
        ref={imgRef}
        src={image.url}
        alt={image.title || 'Galeri Sekolah'}
        loading="lazy"
        className={`img-fade-in ${loaded ? 'loaded' : ''}`}
        onLoad={() => setLoaded(true)}
      />
      <div className="gallery-item-caption">
        <p>{image.title || 'Galeri Sekolah'}</p>
        {image.date && (
          <span className="gallery-item-date">{new Date(image.date).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
        )}
      </div>
    </div>
  );
};

const GaleriAlbumPage = () => {
  const { categorySlug } = useParams();
  const [images, setImages] = useState([]);
  const [categoryData, setCategoryData] = useState(null);
  const [categoryName, setCategoryName] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [imageIndex, setImageIndex] = useState(0);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [favorites, setFavorites] = useState([]);
  const [showOnlyFavorites, setShowOnlyFavorites] = useState(false);
  const observer = useRef();
  const [activeSection, setActiveSection] = useState('all');
  const [copied, setCopied] = useState(false);

  const BATCH_SIZE = 24;

  const fetchAlbumData = useCallback(async (isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setImages([]);
      setLastDoc(null);
      setHasMore(true);
    }
    setError(null);

    try {
      let currentCategoryName = categoryName;

      // 1. Dapatkan nama kategori jika belum ada (untuk load pertama)
      if (!currentCategoryName) {
        const categoryQuery = query(collection(db, 'galeri_kategori'), where("slug", "==", categorySlug));
        const categorySnapshot = await getDocs(categoryQuery);

        if (categorySnapshot.empty) {
          throw new Error("Kategori tidak ditemukan.");
        }

        const data = categorySnapshot.docs[0].data();
        setCategoryData(data);
        currentCategoryName = data.name;
        setCategoryName(currentCategoryName);
      }

      // 2. Dapatkan gambar dengan pagination
      let imagesQuery;
      if (isLoadMore && lastDoc) {
        imagesQuery = query(
          collection(db, 'galeri'),
          where("category", "==", currentCategoryName),
          orderBy("order", "asc"),
          startAfter(lastDoc),
          limit(BATCH_SIZE)
        );
      } else {
        imagesQuery = query(
          collection(db, 'galeri'),
          where("category", "==", currentCategoryName),
          orderBy("order", "asc"),
          limit(BATCH_SIZE)
        );
      }

      const imagesSnapshot = await getDocs(imagesQuery);

      if (imagesSnapshot.empty) {
        setHasMore(false);
      } else {
        const newImages = imagesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setLastDoc(imagesSnapshot.docs[imagesSnapshot.docs.length - 1]);

        if (newImages.length < BATCH_SIZE) {
          setHasMore(false);
        }

        setImages(prev => isLoadMore ? [...prev, ...newImages] : newImages);
      }

    } catch (err) {
      console.error("Error fetching album data: ", err);
      setError(err.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [categorySlug, categoryName, lastDoc]);

  useEffect(() => {
    fetchAlbumData();

    // Reset state when category changes
    const storedFavorites = JSON.parse(localStorage.getItem('gallery_favorites') || '[]');
    setFavorites(storedFavorites);

    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY;
      const scrollPercent = (scrollTop / (documentHeight - windowHeight)) * 100;
      setScrollProgress(scrollPercent);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      setImages([]);
      setLastDoc(null);
      setHasMore(true);
      window.removeEventListener('scroll', handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categorySlug]);

  const toggleFavorite = (e, imageId) => {
    e.stopPropagation();
    const newFavorites = favorites.includes(imageId)
      ? favorites.filter(id => id !== imageId)
      : [...favorites, imageId];

    setFavorites(newFavorites);
    localStorage.setItem('gallery_favorites', JSON.stringify(newFavorites));
  };

  const lastImageElementRef = useCallback(node => {
    if (loading || loadingMore) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        fetchAlbumData(true);
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, loadingMore, hasMore, fetchAlbumData]);

  const Shimmer = () => (
    <div className="gallery-grid">
      {[...Array(8)].map((_, i) => (
        <div key={i} className="gallery-item-skeleton skeleton"></div>
      ))}
    </div>
  );




  const openLightbox = (index) => {
    setImageIndex(index);
    setLightboxOpen(true);
  };

  const handleShare = async () => {
    const shareData = {
      title: `Album: ${categoryName}`,
      text: `Lihat album "${categoryName}" di Galeri SMK Pariwisata Sumbangsih!`,
      url: window.location.href,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.log('Error sharing:', err);
      }
    } else {
      // Fallback: Copy to clipboard
      try {
        await navigator.clipboard.writeText(window.location.href);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy text: ', err);
      }
    }
  };

  if (loading) {
    return <section className="section"><div className="container" style={{ textAlign: 'center' }}>Memuat album...</div></section>;
  }

  if (error) {
    return <NotFoundPage />;
  }

  return (
    <>
      <div className="scroll-progress-container">
        <div className="scroll-progress-bar" style={{ width: `${scrollProgress}%` }}></div>
      </div>
      <PageHeader title={categoryName} subtitle="Album Kegiatan Sekolah" />
      <AnimatedSection>
        <section className="section">
          <div className="container">
            <div style={{ marginBottom: '40px' }}>
              <Link to="/galeri" className="btn-back">
                <FaArrowLeft /> <span>Kembali ke Semua Album</span>
              </Link>
            </div>

            <div className="album-header-container">
              <div className="album-info">
                <h2 style={{ margin: 0, fontSize: '1.8rem' }}>{categoryName}</h2>
                {categoryData && categoryData.date && (
                  <p style={{ margin: '8px 0 0 0', color: '#666', fontSize: '1rem' }}>
                    {new Date(categoryData.date).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}
                    {categoryData.endDate && ` - ${new Date(categoryData.endDate).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}`}
                  </p>
                )}
              </div>
              <div className="album-actions">
                <button
                  className={`btn-secondary ${showOnlyFavorites ? 'active' : ''}`}
                  onClick={() => setShowOnlyFavorites(!showOnlyFavorites)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    backgroundColor: showOnlyFavorites ? 'var(--secondary-color)' : 'transparent',
                    color: showOnlyFavorites ? 'var(--primary-color)' : 'var(--primary-color)',
                    borderColor: showOnlyFavorites ? 'var(--secondary-color)' : 'var(--primary-color)',
                    padding: '8px 16px',
                    borderRadius: '20px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    transition: 'all 0.2s',
                    border: '1px solid'
                  }}
                >
                  {showOnlyFavorites ? <FaHeart /> : <FaRegHeart />}
                  {showOnlyFavorites ? 'Semua' : 'Favorit'}
                </button>
                <button
                  onClick={handleShare}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    backgroundColor: copied ? '#52c41a' : 'transparent',
                    color: copied ? 'white' : 'var(--primary-color)',
                    border: '1px solid',
                    borderColor: copied ? '#52c41a' : 'var(--primary-color)',
                    padding: '8px 16px',
                    borderRadius: '20px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    transition: 'all 0.2s'
                  }}
                  title="Bagikan Album"
                >
                  {copied ? <FaCheck /> : <FaShareAlt />}
                  {copied ? 'Tersalin!' : 'Bagikan'}
                </button>
              </div>
            </div>

            {loading ? (
              <Shimmer />
            ) : (
              <>
                {(() => {
                  const filteredImages = images.filter(img => !showOnlyFavorites || favorites.includes(img.id));

                  const groupedBySection = filteredImages.reduce((acc, img) => {
                    const s = img.section || 'Umum';
                    if (!acc[s]) acc[s] = [];
                    acc[s].push(img);
                    return acc;
                  }, {});

                  const sortedSections = Object.keys(groupedBySection).sort((a, b) => {
                    if (a === 'Umum') return -1;
                    if (b === 'Umum') return 1;
                    return a.localeCompare(b);
                  });

                  // Tab Selection Logic
                  const hasMultipleSections = sortedSections.length > 1 || (sortedSections.length === 1 && sortedSections[0] !== 'Umum');

                  return (
                    <>
                      {/* Section Filter Tabs */}
                      {hasMultipleSections && (
                        <div className="section-tabs-container" style={{
                          position: 'sticky',
                          top: '0',
                          zIndex: 10,
                          background: '#fff',
                          padding: '10px 0',
                          marginBottom: '24px',
                          borderBottom: '1px solid #eee',
                          overflowX: 'auto',
                          whiteSpace: 'nowrap',
                          display: 'flex',
                          gap: '10px',
                          scrollbarWidth: 'none',
                          msOverflowStyle: 'none'
                        }}>
                          <button
                            onClick={() => setActiveSection('all')}
                            style={{
                              padding: '8px 20px',
                              borderRadius: '20px',
                              border: activeSection === 'all' ? 'none' : '1px solid #ddd',
                              background: activeSection === 'all' ? 'var(--primary-color)' : '#f5f5f5',
                              color: activeSection === 'all' ? '#fff' : '#666',
                              cursor: 'pointer',
                              fontWeight: activeSection === 'all' ? 'bold' : 'normal',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              transition: 'all 0.2s ease',
                              flexShrink: 0
                            }}
                          >
                            <FaList /> Semua
                          </button>
                          {sortedSections.map(section => (
                            <button
                              key={section}
                              onClick={() => setActiveSection(section)}
                              style={{
                                padding: '8px 20px',
                                borderRadius: '20px',
                                border: activeSection === section ? 'none' : '1px solid #ddd',
                                background: activeSection === section ? 'var(--primary-color)' : '#f5f5f5',
                                color: activeSection === section ? '#fff' : '#666',
                                cursor: 'pointer',
                                fontWeight: activeSection === section ? 'bold' : 'normal',
                                transition: 'all 0.2s ease',
                                flexShrink: 0
                              }}
                            >
                              {section}
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Content Rendering */}
                      {(() => {
                        const sectionsToRender = activeSection === 'all'
                          ? sortedSections
                          : sortedSections.filter(s => s === activeSection);

                        if (!hasMultipleSections && sectionsToRender.length > 0) {
                          return (
                            <>
                              {activeSection === 'all' && (
                                <div className="gallery-tip">
                                  <FaShareAlt style={{ transform: 'rotate(180deg)' }} />
                                  <div>
                                    <strong>Tips:</strong> Jika gambar belum muncul, harap tunggu sebentar.
                                    Aplikasi ini dioptimalkan untuk menghemat kuota Anda.
                                  </div>
                                </div>
                              )}
                              <div className="gallery-grid">
                                {filteredImages.map((image, index) => (
                                  <GalleryImage
                                    key={`${image.id}-${index}`}
                                    image={image}
                                    isFavorite={favorites.includes(image.id)}
                                    onToggleFavorite={toggleFavorite}
                                    onClick={() => openLightbox(index)}
                                  />
                                ))}
                              </div>
                            </>
                          );
                        }

                        return sectionsToRender.map((section) => {
                          const sectionImages = groupedBySection[section];

                          return (
                            <div key={section} style={{ marginBottom: '50px' }}>
                              {activeSection === 'all' && (
                                <h3 style={{
                                  fontSize: '1.4rem',
                                  color: 'var(--primary-color)',
                                  marginBottom: '20px',
                                  paddingBottom: '10px',
                                  borderBottom: '2px solid var(--secondary-color)',
                                  display: 'inline-block'
                                }}>
                                  {section}
                                </h3>
                              )}
                              <div className="gallery-grid">
                                {sectionImages.map((image, idx) => {
                                  // Find real index for lightbox
                                  // Simplified: if filtering, this is tricky. 
                                  // But wait, the Lightbox slides prop is constructed from ALL filtered images in this component render. 
                                  // See lines 335-337:
                                  // slides={images.filter(img => !showOnlyFavorites || favorites.includes(img.id)) ... }
                                  // This `images` state is the FULL album list.
                                  // `filteredImages` variable here is ALSO `images.filter(...)`.
                                  // So the indices match perfectly with `filteredImages`.
                                  // We just need the index of this image in `filteredImages`.

                                  const lightboxIndex = filteredImages.findIndex(img => img.id === image.id);

                                  return (
                                    <GalleryImage
                                      key={`${image.id}`}
                                      image={image}
                                      isFavorite={favorites.includes(image.id)}
                                      onToggleFavorite={toggleFavorite}
                                      onClick={() => openLightbox(lightboxIndex)}
                                    />
                                  );
                                })}
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </>
                  );

                })()
                }

                {
                  showOnlyFavorites && images.filter(img => favorites.includes(img.id)).length === 0 && (
                    <div style={{ textAlign: 'center', padding: '60px 0', color: '#666' }}>
                      <FaRegHeart style={{ fontSize: '3rem', marginBottom: '20px', opacity: 0.3 }} />
                      <p>Belum ada foto favorit di album ini.</p>
                    </div>
                  )
                }

                {/* Trigger untuk Infinite Scroll */}
                {
                  !showOnlyFavorites && (
                    <div ref={lastImageElementRef} style={{ height: '20px', margin: '20px 0' }}>
                      {loadingMore && <Shimmer />}
                    </div>
                  )
                }
              </>
            )}
          </div>
        </section>
      </AnimatedSection>

      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        slides={images
          .filter(img => !showOnlyFavorites || favorites.includes(img.id))
          .map(img => ({ src: img.url, title: img.title, description: img.description }))}
        index={imageIndex}
        plugins={[Captions, Download, Thumbnails, Fullscreen, Counter, Zoom]}
        thumbnails={{
          position: "bottom",
          width: 80,
          height: 60,
          border: 1,
          borderRadius: 4,
          padding: 4,
          gap: 16,
          showToggle: true,
        }}
        counter={{ container: { style: { top: "unset", bottom: 0 } } }}
        zoom={{
          maxZoomPixelRatio: 5,
          scrollToZoom: true,
        }}
        render={{ buttonZoom: () => null }}
        animation={{ zoom: 500, easing: { zoom: "cubic-bezier(0.25, 0.1, 0.25, 1)" } }}
      />
    </>
  );
};


export default GaleriAlbumPage;